/**
 * CompetitionView - Main Orchestration Component
 *
 * Refactored from 267 lines to ~120 lines by extracting:
 * - useCompetitionState: Local UI state management
 * - useCompetitionData: Data fetching and computed values
 * - CompetitionModals: Modal rendering
 * - CompetitionContent: Main content rendering
 *
 * This component now focuses solely on orchestration.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/useUser';
import { useTenant } from '@/contexts/TenantContext';
import AppHeader from '@/components/layout/AppHeader';
import { TopPageAd, BelowHeaderAd } from '@/components/ads/HeaderAds';
import LobbyBackBar from '@/components/layout/LobbyBackBar';
import { AppHeaderStyleProvider } from '@low6-pickem/ui';
import { isPickemHeaderVisible } from '@low6-pickem/style-config';
import ContestDetailsModal from '@/components/modals/ContestDetailsModal';
import MarketingConsentModal from '@/components/modals/MarketingConsentModal';
import { shouldShowMarketingConsentModal } from '@/constants/marketingConsentCopy';
import { usePickemMarketingConsent, useRecordMarketingConsent } from '@/api/query/usePickem';
import { resolveMarketingConsentHeaderUrl } from '@/utils/marketingConsentHeader';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { CompetitionContent } from './CompetitionContent';
import { CompetitionModals } from './CompetitionModals';
import { IPBlockedDialog } from './IPBlockedDialog';
import { useCompetitionState } from './hooks/useCompetitionState';
import { useCompetitionData } from './hooks/useCompetitionData';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setPageTargeting, clearPageTargeting } from '@/utils/gpt';

interface CompetitionViewProps {
  gameId: string;
  onBack: () => void;
}

export const CompetitionView: React.FC<CompetitionViewProps> = ({ gameId, onBack }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentUser } = useUser();
  const { pickemFullNavigation, tenant } = useTenant();
  // Leaderboard rows use internal DB user UUIDs. AuthContext.user.id is the SSO/
  // Clerk id — matching against that breaks current-user pinning. useUser() exposes
  // the UUID from /api/user, same as the global leaderboard.
  const currentUserId = currentUser?.id;
  const isCircusDaily = tenant?.code === 'cd';
  const navigate = useNavigate();
  const [showContestDetails, setShowContestDetails] = useState(false);

  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const [hasDismissedMarketing, setHasDismissedMarketing] = useState(false);

  useEffect(() => {
    setHasDismissedMarketing(false);
  }, [gameId]);

  // Pass the contest ID to the GPT ad stack as a page-level targeting key so
  // the ad server can target sponsored ads to a specific game/contest. Only
  // set while a specific contest is in context (this view); clear on unmount
  // so the value doesn't leak onto subsequent non-contest pages.
  useEffect(() => {
    if (!gameId) {
      return;
    }

    setPageTargeting({ s5: gameId });

    return () => {
      clearPageTargeting('s5');
    };
  }, [gameId]);

  // Local UI state (modals, leaderboard)
  const {
    modals,
    isLeaderboardOpen,
    openQuickPickModal,
    closeQuickPickModal,
    openFavPickModal,
    closeFavPickModal,
    openClearModal,
    closeClearModal,
    openSuccessModal,
    closeSuccessModal,
    openLeaderboard,
    closeLeaderboard,
  } = useCompetitionState();

  // Data fetching and computed values
  const {
    pickemDetails,
    competitionData,
    isLoading,
    error,
    selections,
    handleTeamSelect,
    handlePropSelect,
    handleOverUnderSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
    favPick,
    buildSubmissionPayload,
    submitMutation,
    hasExistingSelections,
    selectionResults,
    selectionPoints,
    timeDisplay,
    isContestClosed,
    isViewOnly,
    isResultsMode,
  } = useCompetitionData({ gameId, onSuccessSubmit: openSuccessModal });

  // Action handlers
  const handleClear = () => {
    clearSelections();
    closeClearModal();
    toast({
      title: t('toast.selectionsCleared'),
      variant: 'default',
    });
  };

  const handleQuickPick = () => {
    quickPick();
    closeQuickPickModal();
    toast({
      title: t('toast.quickPicks.title'),
      description: t('toast.quickPicks.description'),
      variant: 'success',
    });
  };

  const handleFavPick = () => {
    favPick();
    closeFavPickModal();
    toast({
      title: t('toast.popularPicks.title'),
      description: t('toast.popularPicks.description'),
      variant: 'success',
    });
  };

  const handleSaveChanges = async () => {
    if (isContestClosed) {
      toast({
        title: t('toast.contestClosed.title'),
        description: t('game.contestClosed'),
        variant: 'error',
      });
      return;
    }

    const payload = buildSubmissionPayload();

    if (payload.length === 0) {
      toast({
        title: t('toast.noSelections.title'),
        description: t('toast.noSelections.description'),
        variant: 'error',
      });
      return;
    }

    await submitMutation.mutateAsync({ selections: payload });
  };

  const handleCloseSuccessModal = () => {
    closeSuccessModal();
  };

  const headerStyleConfig = pickemDetails?.pickem?.style_config?.header as
    | Record<string, unknown>
    | undefined;
  const showHeaderBanner = isPickemHeaderVisible(headerStyleConfig);
  const headerBgGradientStart = headerStyleConfig?.gradient_start as string | undefined;
  const headerBgGradientEnd = headerStyleConfig?.gradient_end as string | undefined;
  const headerBgImageUrl = headerStyleConfig?.background_image as string | undefined;
  // Whether the admin configured any banner asset. When the banner is enabled
  // but nothing is configured, fall back to the full default banner (default
  // gradient + clouds + default logo); otherwise show only configured assets
  // (suppress the defaults so they don't mix/flash) — DKPLAT-461.
  const headerHasAssets = Boolean(
    String(headerStyleConfig?.logo ?? '').trim() ||
      String(headerBgImageUrl ?? '').trim() ||
      String(headerBgGradientStart ?? '').trim() ||
      String(headerBgGradientEnd ?? '').trim()
  );

  // Feature-gate flags from style_config. Default OFF: absent or false ⇒ hidden.
  const showPopularPicks =
    (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)
      ?.showPopularPicks === true;
  const showTermsCheckbox =
    (pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined)
      ?.showTermsCheckbox === true;

  const styleConfig = pickemDetails?.pickem?.style_config as Record<string, unknown> | undefined;
  const showMarketingConsentModal = shouldShowMarketingConsentModal(styleConfig);
  const { data: marketingConsentData } = usePickemMarketingConsent(
    gameId,
    showMarketingConsentModal
  );
  const recordMarketingConsent = useRecordMarketingConsent(gameId);
  const marketingConsentVersion = marketingConsentData?.consent ?? null;
  const marketingConsentConfig = styleConfig?.marketingConsent as
    | Record<string, unknown>
    | undefined;
  const hasPersistedMarketingConsent = marketingConsentData?.marketing_consent_given === true;
  const marketingModalOpen =
    showMarketingConsentModal &&
    !!marketingConsentVersion &&
    !hasPersistedMarketingConsent &&
    !hasDismissedMarketing;
  const marketingHeaderImageUrl =
    resolveMarketingConsentHeaderUrl(marketingConsentVersion) ||
    (marketingConsentConfig?.headerImageUrl as string | undefined) ||
    headerStyleConfig?.background_image ||
    pickemDetails?.pickem?.text_main_image_en ||
    null;

  const handleMarketingAccept = async () => {
    try {
      await recordMarketingConsent.mutateAsync(true);
      setHasDismissedMarketing(true);
    } catch {
      toast({
        title: t('toast.consentSaveFailed.title'),
        description: t('toast.consentSaveFailed.description'),
        variant: 'error',
      });
    }
  };

  const handleMarketingClose = () => {
    setHasDismissedMarketing(true);
  };

  // Loading state
  if (isLoading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState onBack={onBack} />;
  }

  return (
    <AppHeaderStyleProvider
      value={{ headerBgGradientStart, headerBgGradientEnd, headerBgImageUrl }}
    >
      <div
        className={`flex flex-col ${marketingModalOpen ? 'pointer-events-none select-none' : ''}`}
      >
        {/* "Back to Lobby" bar is only for the external lobby flow (full navigation
            OFF — e.g. DK via the lobby endpoint). Full-navigation tenants (SkyPicks)
            use the in-app Lobby breadcrumb instead; showing this bar there sent them
            to an external lobby URL they don't use (DKPLAT-461). */}
        {/* Top-of-page ad placement */}
        <TopPageAd />
        {!pickemFullNavigation && !isCircusDaily && <LobbyBackBar />}
        {/* App Header — hidden when banner styling is disabled or empty (DKPLAT-437) */}
        {showHeaderBanner && (
          <>
            <AppHeader
              showShadow
              // Full-navigation tenants (SkyPicks) navigate back via the header arrow
              // (their in-app lobby). Non-full-nav (DK) uses the "Back to Lobby" bar
              // instead, so the header arrow stays hidden there (DKPLAT-461).
              showBackButton={pickemFullNavigation}
              onBackClick={onBack}
              onInfoClick={() => setShowContestDetails(true)}
              headerLogoUrl={headerStyleConfig?.logo as string | undefined}
              suppressFallbacks={headerHasAssets}
            />

            {/* Below-header ad placement */}
            <BelowHeaderAd />
          </>
        )}

        {/* Main Content */}
        <CompetitionContent
          pickemDetails={pickemDetails}
          competitionData={competitionData}
          timeDisplay={timeDisplay}
          selections={selections}
          selectionResults={selectionResults}
          selectionPoints={selectionPoints}
          currentUserId={currentUserId}
          onTeamSelect={handleTeamSelect}
          onPropSelect={handlePropSelect}
          onOverUnderSelect={handleOverUnderSelect}
          onTieBreakerChange={handleTieBreakerChange}
          onQuickPick={openQuickPickModal}
          onFavouritePick={openFavPickModal}
          onClear={openClearModal}
          onSave={handleSaveChanges}
          isContestClosed={isContestClosed}
          isViewOnly={isViewOnly}
          isResultsMode={isResultsMode}
          isSaving={submitMutation.isPending}
          showLeaderBoardOnLoad={mode === 'leaderboard'}
          hasEntered={hasExistingSelections}
          onBack={onBack}
          showPopularPicks={showPopularPicks}
          showTermsCheckbox={showTermsCheckbox}
          showInfoOnGameCard={!showHeaderBanner}
          onInfoClick={() => setShowContestDetails(true)}
        />

        {/* All Modals */}
        <CompetitionModals
          isQuickPickOpen={modals.quickPick}
          isFavPickOpen={modals.favPick}
          isClearOpen={modals.clear}
          isSuccessOpen={modals.success}
          isLeaderboardOpen={isLeaderboardOpen}
          onCloseQuickPick={closeQuickPickModal}
          onCloseFavPick={closeFavPickModal}
          onCloseClear={closeClearModal}
          onCloseSuccess={handleCloseSuccessModal}
          onCloseLeaderboard={closeLeaderboard}
          onConfirmQuickPick={handleQuickPick}
          onConfirmFavPick={handleFavPick}
          onConfirmClear={handleClear}
          onReturnToLobby={() => navigate('/main/myPicks')}
          gameId={gameId}
          currentUserId={currentUserId}
          hasExistingSelections={hasExistingSelections}
        />

        {/* Contest Details Modal */}
        <ContestDetailsModal
          isOpen={showContestDetails}
          onClose={() => setShowContestDetails(false)}
          pickemDetails={pickemDetails}
          competitionData={competitionData}
        />

        {/* IP Blocked Dialog - Full Screen Overlay */}
        <IPBlockedDialog
          isOpen={!!submitMutation.ipBlockedError}
          errorMessage={submitMutation.ipBlockedError || undefined}
          onClose={submitMutation.clearIpBlockedError}
        />
      </div>

      <MarketingConsentModal
        isOpen={marketingModalOpen}
        onAccept={handleMarketingAccept}
        onClose={handleMarketingClose}
        headerImageUrl={marketingHeaderImageUrl}
        consentTextHtml={marketingConsentVersion?.consent_text}
        headline={marketingConsentConfig?.headline as string | undefined}
        subheadline={marketingConsentConfig?.subheadline as string | undefined}
        checkboxPrompt={marketingConsentConfig?.checkboxPrompt as string | undefined}
        eligibilityText={marketingConsentConfig?.eligibilityText as string | undefined}
        footerText={marketingConsentConfig?.footerText as string | undefined}
        buttonText={marketingConsentConfig?.buttonText as string | undefined}
      />
    </AppHeaderStyleProvider>
  );
};

export default CompetitionView;
